import { useState } from "react";
import { CERT_STATE_LABELS, type PickCompanyDto } from "@klantpunt/shared";
import { Combobox, type ComboItem } from "../../app/Combobox";
import { hasModule, useMe } from "../../lib/auth";
import { useDirectoryPick } from "./Pick";
import { QuickCompanyDialog, QuickPersonDialog } from "./QuickAdd";

/**
 * Eén kiezer voor wie iets doet (uitvoerder van een taak, keuring, onderhoud, leverancier van een contract): leden en
 * bedrijven met contactpersonen uit het adresboek, als zoekkiezer (docs/UX-RICHTLIJNEN.md §6, "Kiezer voor bedrijven
 * en personen"). Met module Adresboek alleen uit het adresboek; een nieuwe leverancier voeg je hier snel toe.
 */
export interface Party {
  companyId: string | null;
  personId: string | null;
  /** Lid van de organisatie; alleen waar `users` is meegegeven. */
  userId: string | null;
  /** Vrije naam; leeg bij een keuze uit het adresboek of de leden. */
  text: string;
}
export const NO_PARTY: Party = { companyId: null, personId: null, userId: null, text: "" };

/** `pick`: keuze uit een lijst (direct opslaan mag); `type`: invullen in het vrije veld (opslaan bij verlaten). */
export type PartyChange = "pick" | "type";

const TEXT = "__text";
const companyValue = (id: string) => `c:${id}`;
const companyOf = (value: string) => (value.startsWith("c:") ? value.slice(2) : null);
const norm = (s: string) => s.trim().toLocaleLowerCase("nl");

/** Naam van de persoon uit een weergavenaam "Persoon (Bedrijf)". */
export const personNameOf = (text: string | null | undefined, companyName: string | undefined) =>
  text && companyName && text.endsWith(` (${companyName})`) ? text.slice(0, -companyName.length - 3) : (text ?? "");

/** Bedrijf (en eventueel persoon) uit het adresboek waarvan de naam precies overeenkomt met wat er getypt is. */
export function matchParty(text: string, list: PickCompanyDto[] | undefined): Party | null {
  const t = norm(text);
  if (!t || !list) return null;
  for (const c of list) {
    if (norm(c.name) === t) return { ...NO_PARTY, companyId: c.id };
    for (const p of c.people)
      if (norm(`${p.name} (${c.name})`) === t) return { ...NO_PARTY, companyId: c.id, personId: p.id };
  }
  return null;
}

export function PartyPicker({
  id,
  label = "Uitvoerder",
  value,
  onChange,
  onTextBlur,
  savedName,
  buildingId,
  users,
  emptyLabel = "Nog niet bekend",
  placeholder = "bijv. Liftinstituut",
  clearable = true,
  withPerson = true,
  internal = true,
  disabled,
  error,
  className,
}: {
  /** Basis voor de id's van de velden (uniek op de pagina). */
  id: string;
  label?: string;
  value: Party;
  onChange: (p: Party, how: PartyChange) => void;
  /** Vrije naam verlaten (alleen zonder module Adresboek), bijv. om op te slaan. */
  onTextBlur?: (text: string) => void;
  /** Opgeslagen weergavenaam: houdt een gearchiveerd bedrijf of persoon zichtbaar in de lijst. */
  savedName?: string | null;
  buildingId?: string;
  /** Leden die ook gekozen kunnen worden (taken). */
  users?: { id: string; name: string }[];
  emptyLabel?: string;
  placeholder?: string;
  /** Mag het veld leeg (keuze `emptyLabel`)? Uit bij een verplichte leverancier. */
  clearable?: boolean;
  /** Contactpersoon kiezen na een bedrijf (uit bij bijv. de leverancier van een artikel). */
  withPerson?: boolean;
  /** Eigen dienst als keuze tonen. */
  internal?: boolean;
  disabled?: boolean;
  error?: string;
  /** Extra klasse voor elk veld, bijv. `wide` in een formulierraster. */
  className?: string;
}) {
  const { data: me } = useMe();
  const withDirectory = hasModule(me, "directory");
  const canAdd = hasModule(me, "directory", "edit");
  const directory = useDirectoryPick({ kind: "supplier", buildingId });
  const [adding, setAdding] = useState<{ kind: "company" | "person"; name: string } | null>(null);
  const fieldClass = className ? `field ${className}` : "field";
  const company = directory.data?.find((c) => c.id === value.companyId);
  const err = error && <span className="hint err">{error}</span>;

  // Zonder adresboek en zonder leden blijft het één tekstveld.
  if (!withDirectory && !users?.length)
    return (
      <label className={fieldClass}>
        <span className="label">{label}</span>
        <input
          id={`${id}-text`}
          value={value.text}
          disabled={disabled}
          placeholder={placeholder}
          maxLength={200}
          autoComplete="organization"
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange({ ...NO_PARTY, text: e.target.value }, "type")}
          onBlur={(e) => onTextBlur?.(e.target.value)}
        />
        {err}
      </label>
    );

  const items: ComboItem[] = [];
  for (const u of users ?? []) items.push({ value: u.id, label: u.name, group: "Gebruikers" });
  if (withDirectory) items.push(...companyItems(directory.data, { internal }));
  if (value.companyId && !items.some((i) => i.value === companyValue(value.companyId!)))
    items.unshift({
      value: companyValue(value.companyId),
      label: value.personId ? "Bedrijf" : (savedName ?? "Bedrijf"),
    });
  // Oude vrije naam (van vóór het adresboek): zichtbaar houden tot er een bedrijf gekozen is.
  const legacy = Boolean(value.text) && !value.companyId && !value.userId;
  if (legacy)
    items.unshift({ value: TEXT, label: value.text, meta: withDirectory ? "Staat niet in het adresboek" : undefined });
  const selected = value.companyId ? companyValue(value.companyId) : value.userId ? value.userId : legacy ? TEXT : null;
  const showPerson = withPerson && Boolean(value.companyId && (company || value.personId));

  const noun = label.replace(/\s*\*$/, "").toLocaleLowerCase("nl");
  const addProps = withDirectory
    ? canAdd
      ? {
          onAdd: (q: string) => setAdding({ kind: "company", name: q || (legacy ? value.text : "") }),
          addLabel: (q: string) => (q ? `Nieuwe leverancier “${q}”` : "Nieuwe leverancier"),
        }
      : {}
    : {
        // Zonder adresboek: een andere naam mag, zoals vroeger "Anders (naam invullen)".
        onAdd: (q: string) => {
          if (!q) return;
          onChange({ ...NO_PARTY, text: q }, "type");
          onTextBlur?.(q);
        },
        addLabel: (q: string) => (q ? `“${q}” gebruiken` : `Typ een naam om een andere ${noun} te gebruiken`),
      };

  return (
    <>
      <Combobox
        id={id}
        label={label}
        items={items}
        value={selected}
        disabled={disabled}
        className={className}
        placeholder={withDirectory ? "Zoek op naam of vakgebied" : placeholder}
        clearLabel={clearable ? emptyLabel : undefined}
        error={error}
        hint={
          legacy && withDirectory && canAdd ? (
            <>
              Deze naam staat niet in het adresboek.{" "}
              <button
                type="button"
                className="linkbtn"
                onClick={() => setAdding({ kind: "company", name: value.text })}
              >
                Toevoegen aan het adresboek
              </button>
            </>
          ) : withDirectory && !canAdd ? (
            "Staat de leverancier er niet bij? Vraag iemand die het adresboek beheert om die toe te voegen."
          ) : undefined
        }
        onChange={(v) => {
          const c = v ? companyOf(v) : null;
          if (v === TEXT) return;
          if (c) onChange({ ...NO_PARTY, companyId: c }, "pick");
          else if (v) onChange({ ...NO_PARTY, userId: v }, "pick");
          else onChange(NO_PARTY, "pick");
        }}
        {...addProps}
      />
      {showPerson && (
        <Combobox
          id={`${id}-person`}
          label="Contactpersoon"
          className={className}
          disabled={disabled}
          placeholder="Geen vaste contactpersoon"
          items={personItems(
            company,
            value.personId
              ? { id: value.personId, name: personNameOf(savedName, company?.name) || "Contactpersoon" }
              : null,
          )}
          value={value.personId}
          clearLabel="Geen vaste contactpersoon"
          onChange={(p) => onChange({ ...value, userId: null, text: "", personId: p }, "pick")}
          {...(canAdd && company
            ? {
                onAdd: (q: string) => setAdding({ kind: "person", name: q }),
                addLabel: (q: string) => (q ? `Nieuwe contactpersoon “${q}”` : "Nieuwe contactpersoon"),
              }
            : {})}
        />
      )}
      {adding?.kind === "company" && (
        <QuickCompanyDialog
          initialName={adding.name}
          onClose={() => setAdding(null)}
          onSaved={(companyId, personId) => {
            setAdding(null);
            onChange({ ...NO_PARTY, companyId, personId }, "pick");
          }}
        />
      )}
      {adding?.kind === "person" && company && (
        <QuickPersonDialog
          initialName={adding.name}
          companyId={company.id}
          companyName={company.name}
          onClose={() => setAdding(null)}
          onSaved={(personId) => {
            setAdding(null);
            onChange({ ...value, userId: null, text: "", personId }, "pick");
          }}
        />
      )}
    </>
  );
}

/** Bedrijven uit `/directory/pick` als kaartjes voor de zoekkiezer: vaste leveranciers van het gebouw, eigen dienst, adresboek. */
export function companyItems(data: PickCompanyDto[] | undefined, opts: { internal?: boolean } = {}): ComboItem[] {
  const list = data ?? [];
  const groups: [string, PickCompanyDto[]][] = [
    ["Vaste leveranciers van dit gebouw", list.filter((c) => c.linked && !c.isInternal)],
    ["Eigen dienst", opts.internal === false ? [] : list.filter((c) => c.isInternal)],
    ["Adresboek", list.filter((c) => !c.linked && !c.isInternal)],
  ];
  return groups.flatMap(([group, cs]) =>
    cs.map((c) => ({
      value: companyValue(c.id),
      label: c.name,
      group,
      meta: [c.isInternal ? "" : c.trades.join(", "), c.phone ?? ""].filter(Boolean).join(" · ") || undefined,
      badge:
        c.certState === "warn" || c.certState === "expired" ? (
          <span className={`chip dir-cert dir-cert-${c.certState}`}>{CERT_STATE_LABELS[c.certState]}</span>
        ) : undefined,
    })),
  );
}

function personItems(company: PickCompanyDto | undefined, current: { id: string; name: string } | null): ComboItem[] {
  const ppl = company?.people ?? [];
  const items: ComboItem[] = ppl.map((p) => ({
    value: p.id,
    label: p.name,
    meta: [p.jobTitle ?? "", p.mobile ?? p.phone ?? ""].filter(Boolean).join(" · ") || undefined,
  }));
  if (current && !ppl.some((p) => p.id === current.id)) items.unshift({ value: current.id, label: current.name });
  return items;
}
